/**
 * Avatar — serif initials fallback. Hairline border, no shadow.
 * If `src` is provided, renders an <img>; otherwise the initials of
 * `name` or the first character of `email`.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  name?: string | null;
  email?: string | null;
  size?: number;
}

function initialsOf(name?: string | null, email?: string | null): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => (p[0] ?? '').toUpperCase()).join('') || '·';
  }
  if (email) return (email[0] ?? '·').toUpperCase();
  return '·';
}

export function Avatar({
  src,
  name,
  email,
  size = 32,
  className,
  ...rest
}: AvatarProps) {
  const initials = initialsOf(name, email);
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center',
        'border border-ink-200 bg-surface overflow-hidden select-none',
        'rounded-full',
        className,
      )}
      style={{ width: size, height: size }}
      aria-label={name || email || 'Account avatar'}
      {...rest}
    >
      {src ? (
        <img
          src={src}
          alt={name || email || ''}
          className="h-full w-full object-cover"
        />
      ) : (
        <span
          className="font-serif text-ink-700"
          style={{ fontSize: Math.max(11, Math.floor(size * 0.42)) }}
        >
          {initials}
        </span>
      )}
    </div>
  );
}
